import { storage } from './storage.js';
import { normalizeStoredCharTokens } from './character-name-tokens.js';

const USER_PLACEHOLDER_TOKEN = '{user}';
const PLACEHOLDER_KEYS = ['user', 'char', 'char1', 'char2', 'char3'];

const normalizeUserName = (value) => {
  if (typeof value !== 'string') return '';
  return value.trim();
};

const pickCharacterName = (character) => {
  if (!character || typeof character !== 'object') return '';
  return normalizeUserName(character.name || character.character_name || character.char_name || '');
};

const replaceToken = (inputText, token, value) => {
  if (!inputText.includes(token)) return inputText;
  return inputText.split(token).join(value);
};

export const hasCharacterPlaceholder = (text) => {
  if (typeof text !== 'string' || text.length === 0) return false;
  return ['char', 'char1', 'char2', 'char3'].some((key) => (
    text.includes(`{${key}}`) || text.includes(`{{${key}}}`)
  ));
};

export const hasAnySupportedPlaceholder = (text) => {
  if (typeof text !== 'string' || text.length === 0) return false;
  return PLACEHOLDER_KEYS.some((key) => (
    text.includes(`{${key}}`) || text.includes(`{{${key}}}`)
  ));
};

export const resolveUserNameFromStorage = ({ fallback = 'User', preserveToken = false } = {}) => {
  const userData = storage.get('userData') || {};
  const normalizedName = normalizeUserName(userData?.name || '');
  if (normalizedName) return normalizedName;
  if (preserveToken) return USER_PLACEHOLDER_TOKEN;
  return fallback;
};

export const buildCharacterPlaceholderMap = ({ scenarioChars = [], fallbackCharName = 'Character' } = {}) => {
  const chars = Array.isArray(scenarioChars) ? scenarioChars : [];
  const char1 = pickCharacterName(chars[0]) || fallbackCharName;
  const char2 = pickCharacterName(chars[1]) || char1;
  const char3 = pickCharacterName(chars[2]) || char1;

  return {
    char: char1,
    char1,
    char2,
    char3
  };
};

export const resolveTextPlaceholders = (
  text,
  { userName, scenarioChars = [], fallbackCharName = 'Character', preserveUserTokenWhenMissing = false } = {}
) => {
  if (typeof text !== 'string' || text.length === 0) return '';
  const canonicalText = normalizeStoredCharTokens(text);
  if (!hasAnySupportedPlaceholder(canonicalText)) return canonicalText;

  const resolvedUserName = normalizeUserName(userName || '')
    || resolveUserNameFromStorage({
      fallback: 'User',
      preserveToken: preserveUserTokenWhenMissing
    });
  const characterMap = buildCharacterPlaceholderMap({ scenarioChars, fallbackCharName });
  const valueByKey = { user: resolvedUserName, ...characterMap };

  return PLACEHOLDER_KEYS.reduce((nextText, key) => {
    const replacementValue = String(valueByKey[key] ?? '');
    const singleBraceToken = `{${key}}`;
    const doubleBraceToken = `{{${key}}}`;
    const withDoubleBrace = replaceToken(nextText, doubleBraceToken, replacementValue);
    return replaceToken(withDoubleBrace, singleBraceToken, replacementValue);
  }, canonicalText);
};
